import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {t} from 'i18next'
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Row, Col, Button, ModalBody, ModalHeader, ModalFooter, Input, Label } from 'reactstrap'
import axios from 'axios';

import { headersState, multiCheckLoading } from '../../../recoil/recoil';
import env from '../../../env/src_config';
import { isNull, getTimezoneOffset, displayDate } from '../../../izUtils';
import { axiosError, errorStatus } from '../../../helpers/response';
import { transformer, linker } from '../../../helpers/fields';
import Spinner from '../../spinner/Spinner';
import Loader from '../../spinner/Loader';

const Export = ({ setExportModalOpen }) => {
    const navigate = useNavigate()

    const setMultiCheckLoading = useSetRecoilState(multiCheckLoading);
    const headers = useRecoilValue(headersState);
    const [inputs, setInputs] = useState(null);
    const [showErrors, setShowErrors] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [selectAllTechnicians, setSelectAllTechnicians] = useState(false);

    const lsFilterName = 'AKODA.scheduleFilter';

    useEffect(() => {
        let lsFilter = JSON.parse(localStorage.getItem(lsFilterName));
        let modalFilters = null;

        if (!isNull(lsFilter) && !isNull(lsFilter.technician) && !isNull(lsFilter.technician.value) && lsFilter.technician.value.length !== 0) {
            modalFilters = { technicians: { value: [lsFilter.technician.value] } };
        }

        updateData(false, modalFilters);

    }, []) // eslint-disable-line


    useEffect(() => {
        if (!isNull(inputs) && !isNull(inputs.technicians) && !isNull(inputs.technicians.values)) {
            const allTechnicianIds = inputs.technicians.values.map(tech => tech.id);
            const currentSelected = inputs.technicians.value || [];
            const isAllSelected = allTechnicianIds.length > 0 && allTechnicianIds.every(id => currentSelected.includes(id));
            setSelectAllTechnicians(isAllSelected);
        }
    }, [inputs])

    const updateData = (save, passedInputs) => {
        if (save) setExportLoading(true)
        let payload = { save };

        if (!isNull(passedInputs)) {
            payload.data = {};
            const keys = Object.keys(passedInputs);

            for (let i = 0; i < keys.length; i++) {
                if (!isNull(passedInputs[keys[i]].value) && passedInputs[keys[i]].value.length !== 0) {
                    payload.data[keys[i]] = passedInputs[keys[i]].value;
                } else {
                    payload.data[keys[i]] = "";
                }
            }

            payload.data.time_adjustment = getTimezoneOffset();
        }

        let sendHeaders = { headers: {...headers} };
        if (save) {
            sendHeaders.responseType = 'blob';
        }

        axios.post(env.api + '/api/task/export', payload, sendHeaders).then(async response => {
            if (save) {
                // Check if the blob is actually JSON (error response)
                if (response.data.type === 'application/json') {
                    // Parse the blob as JSON to get the error message
                    const text = await response.data.text();
                    const errorData = JSON.parse(text);
                    errorStatus(errorData, t);
                    setShowErrors(true);
                } else if (response.data.type.includes('spreadsheetml.sheet')) {
                    const href = window.URL.createObjectURL(response.data);
                    const a = document.createElement('a');
                    a.setAttribute('download', `${displayDate(payload.data.from, 'date')}-${displayDate(payload.data.to, 'date')}.xlsx`);
                    a.href = href;
                    a.click();
                    window.URL.revokeObjectURL(href); // Clean up the object URL
                } else {
                    errorStatus(response.data, t);
                    setShowErrors(true);
                }

                setExportLoading(false);
            }

            setMultiCheckLoading(false) // Set loading for field MultiCheck to false
            if (!isNull(response.data.data)) {
                setInputs(transformer(response.data.data));
            }
        }).catch(error => {
            axiosError(error, navigate);
            setExportLoading(false)
        });
    }

    const textChange = (value, name) => {
        let clonedInputs = {...inputs}
        clonedInputs[name].value = value;
        updateData(false, clonedInputs);
    }

    const multiChange = (value, name, component) => {
        setMultiCheckLoading(name + '-' + component?.id)
        let clonedInputs = {...inputs};
        if (clonedInputs[name].value.length === 0) {
            clonedInputs[name].value.push(value)
        } else {
            if (clonedInputs[name].value.indexOf(value) === -1) {
                clonedInputs[name].value.push(value);
            } else {
                const filteredValues = clonedInputs[name].value.filter(el => el !== value);
                clonedInputs[name].value = filteredValues;
            }
        }

        if (name === 'technicians' && !isNull(clonedInputs[name].values)) {
            const allTechnicianIds = clonedInputs[name].values.map(tech => tech.id);
            const isAllSelected = allTechnicianIds.every(id => clonedInputs[name].value.includes(id));
            setSelectAllTechnicians(isAllSelected);
        }

        updateData(false, clonedInputs);
    }

    const handleSelectAllTechnicians = () => {
        if (!isNull(inputs) && !isNull(inputs.technicians)) {
            let clonedInputs = {...inputs};
            const allTechnicianIds = clonedInputs.technicians.values.map(tech => tech.id);

            if (selectAllTechnicians) {
                clonedInputs.technicians.value = [];
            } else {
                clonedInputs.technicians.value = [...allTechnicianIds];
            }

            setSelectAllTechnicians(!selectAllTechnicians);
            updateData(false, clonedInputs);
        }
    }

    // Create fields
    const mapLinker = (field) => {
        const inputlinkerFields = {
            field,
            inputs,
            showErrors,
            component: {id: 'export'},
            selectHandler: (value, id) => textChange(value, id),
            multiHandler: (value, id, component) => multiChange(value, id, component),
            dateHandler: (value, id) => textChange(value, id),
        }

        return linker(inputlinkerFields);
    }

    if (isNull(inputs)) return <Spinner />
    return (
        <Fragment>
            {exportLoading && <Loader />}
            <ModalHeader toggle={setExportModalOpen}>{t('schedule.export.title')}</ModalHeader>
            <ModalBody>
                <div className="theme-form">
                    <Row>
                        <Col sm="12">

                            { ['time_adjustment', 'from', 'to'].map(field => mapLinker(field)) }

                            {!isNull(inputs) && !isNull(inputs.technicians) && !isNull(inputs.technicians.values) && inputs.technicians.values.length > 0 &&
                                <div className="form-checkbox-group mb-2" style={{ marginLeft: '21px' }}>
                                    <Input
                                        className="custom-control-input"
                                        id="select-all-technicians-top"
                                        type="checkbox"
                                        onChange={handleSelectAllTechnicians}
                                        checked={selectAllTechnicians}
                                    />
                                    <Label className="checkmark" htmlFor="select-all-technicians-top"></Label>
                                    <Label className="custom-control-label" htmlFor="select-all-technicians-top" style={{ cursor: 'pointer', fontWeight: '500' }}>
                                        {t('schedule.export.selectAllTechnicians')}
                                    </Label>
                                </div>
                            }

                            { mapLinker('technicians') }

                            {!isNull(inputs) && !isNull(inputs.technicians) && !isNull(inputs.technicians.values) && inputs.technicians.values.length > 0 &&
                                <div className="form-checkbox-group mb-2" style={{ marginLeft: '21px' }}>
                                    <Input
                                        className="custom-control-input"
                                        id="select-all-technicians-bottom"
                                        type="checkbox"
                                        onChange={handleSelectAllTechnicians}
                                        checked={selectAllTechnicians}
                                    />
                                    <Label className="checkmark" htmlFor="select-all-technicians-bottom"></Label>
                                    <Label className="custom-control-label" htmlFor="select-all-technicians-bottom" style={{ cursor: 'pointer', fontWeight: '500' }}>
                                        {t('schedule.export.selectAllTechnicians')}
                                    </Label>
                                </div>
                            }

                            { ['service_type', 'service_service_type', 'user_department', 'exception_type', 'exception_other'].map(field => mapLinker(field)) }
                        </Col>
                    </Row>
                </div>
            </ModalBody>
            <ModalFooter>
                <div className='w-100 d-flex justify-content-between'>
                    <Button color="secondary" onClick={setExportModalOpen}>{t('close')}</Button>
                    <Button color="primary" onClick={() => updateData(true, inputs)}>{t('export')}</Button>
                </div>
            </ModalFooter>
        </Fragment>
    )
}

export default Export